import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { useNotify } from '../../../lib/notify';
import { applyApiErrors } from '../lib/formErrors';
import {
  apiFieldPath,
  CHANNELS,
  describeTiming,
  KIND_OPTIONS,
  KINDS,
  ruleDefaults,
  ruleFormSchema,
  toCreateRequest,
  toUpdateRequest,
  unknownPlaceholders,
  type ReminderRule,
  type RuleFormInput,
  type RuleFormValues,
} from '../lib/reminderRule';

/** Create or edit a reminder rule. The kind is fixed once a rule exists. */
export function RuleDialog({
  open,
  rule,
  onClose,
}: {
  open: boolean;
  rule: ReminderRule | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const notify = useNotify();
  const { can } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const editing = Boolean(rule);
  const templateRef = useRef<HTMLTextAreaElement | null>(null);
  // One key per new rule, so a double submit or retry cannot create it twice.
  const idemKey = useRef(crypto.randomUUID());

  const form = useForm<RuleFormInput, unknown, RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    values: ruleDefaults(rule),
  });
  const { control, handleSubmit, setError, setValue, getValues } = form;
  const kind = useWatch({ control, name: 'kind' });
  const offsetDays = useWatch({ control, name: 'offsetDays' });
  const repeat = useWatch({ control, name: 'repeatEveryDays' });
  const template = useWatch({ control, name: 'template' });
  const info = KINDS[kind];

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => unwrap(api.GET('/roles')),
    enabled: open && can('staff.view'),
    staleTime: 60_000,
  });
  const staff = useQuery({
    queryKey: ['staff', 'all-active'],
    queryFn: () =>
      unwrap(api.GET('/staff', { params: { query: { pageSize: 100, status: 'ACTIVE' } } })),
    enabled: open && can('staff.view'),
    staleTime: 60_000,
  });
  const catalog = useQuery({
    queryKey: ['reminder-placeholders'],
    queryFn: () => unwrap(api.GET('/reminder-rules/placeholders')),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const kindCatalog = catalog.data?.data.find((c) => c.kind === kind);
  const placeholders = kindCatalog?.placeholders ?? [];
  const unknown = placeholders.length ? unknownPlaceholders(template, placeholders) : [];

  const save = useMutation({
    mutationFn: (v: RuleFormValues) =>
      rule
        ? unwrap(
            api.PATCH('/reminder-rules/{id}', {
              params: { path: { id: rule.id } },
              body: toUpdateRequest(v, rule.version),
            }),
          )
        : unwrap(
            api.POST('/reminder-rules', {
              params: { header: { 'idempotency-key': idemKey.current } },
              body: toCreateRequest(v),
            }),
          ),
    onSuccess: (r) => {
      idemKey.current = crypto.randomUUID();
      void qc.invalidateQueries({ queryKey: ['reminder-rules'] });
      notify.success(editing ? `Saved “${r.name}”` : `Created “${r.name}”`);
      onClose();
    },
    onError: (e) => {
      if (!applyApiErrors(e, setError, apiFieldPath)) notify.error(e);
    },
  });

  const insertPlaceholder = (ph: string) => {
    const el = templateRef.current;
    const cur = getValues('template');
    const token = `{{${ph}}}`;
    const at = el?.selectionStart ?? cur.length;
    const end = el?.selectionEnd ?? at;
    setValue('template', cur.slice(0, at) + token + cur.slice(end), { shouldDirty: true });
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(at + token.length, at + token.length);
    });
  };

  const roleOptions = roles.data?.data ?? [];
  const staffOptions = staff.data?.data ?? [];

  return (
    <Dialog
      open={open}
      onClose={save.isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <form onSubmit={(e) => void handleSubmit((v) => save.mutate(v))(e)} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit reminder' : 'New reminder'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Name"
                  autoFocus={!editing}
                  placeholder="e.g. Overdue invoices – weekly nudge"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="kind"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  label="Remind me about"
                  disabled={editing}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? KINDS[field.value].description}
                >
                  {KIND_OPTIONS.map((k) => (
                    <MenuItem key={k.value} value={k.value}>
                      {k.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {info.timing !== 'none' && (
                <Controller
                  name="offsetDays"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      label={`Days ${info.timing}`}
                      value={Number.isNaN(field.value) ? '' : field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? NaN : Number(e.target.value))
                      }
                      slotProps={{ htmlInput: { min: 0, max: 365, step: 1 } }}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message ?? ' '}
                      fullWidth
                    />
                  )}
                />
              )}
              <Controller
                name="repeatEveryDays"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="number"
                    label="Repeat every (days)"
                    value={field.value === null || Number.isNaN(field.value) ? '' : field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : Number(e.target.value))
                    }
                    slotProps={{ htmlInput: { min: 1, max: 365, step: 1 } }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message ?? 'Leave empty to remind once'}
                    fullWidth
                  />
                )}
              />
            </Stack>
            <Alert severity="info" icon={false} sx={{ py: 0.25 }}>
              {describeTiming({
                kind,
                offsetDays:
                  typeof offsetDays === 'number' && !Number.isNaN(offsetDays) ? offsetDays : 0,
                repeatEveryDays:
                  typeof repeat === 'number' && !Number.isNaN(repeat) ? repeat : null,
              })}
            </Alert>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Channels
              </Typography>
              <Controller
                name="channels"
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', columnGap: 1 }}>
                      {CHANNELS.map((c) => (
                        <FormControlLabel
                          key={c.value}
                          label={c.label}
                          control={
                            <Checkbox
                              checked={field.value.includes(c.value)}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, c.value]
                                    : field.value.filter((x) => x !== c.value),
                                )
                              }
                            />
                          }
                        />
                      ))}
                    </Stack>
                    {fieldState.error && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Box>

            <Controller
              name="recipientRoleIds"
              control={control}
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  options={roleOptions.map((r) => r.id)}
                  value={field.value}
                  onChange={(_, v) => field.onChange(v)}
                  getOptionLabel={(id) => roleOptions.find((r) => r.id === id)?.name ?? 'Role'}
                  loading={roles.isLoading}
                  disabled={!can('staff.view')}
                  renderValue={(value, getItemProps) =>
                    value.map((id, index) => {
                      const { key, ...rest } = getItemProps({ index });
                      return (
                        <Chip
                          key={key}
                          size="small"
                          label={roleOptions.find((r) => r.id === id)?.name ?? 'Role'}
                          {...rest}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Notify roles"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message ?? 'Everyone with these roles gets it'}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="recipientUserIds"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={staffOptions.map((s) => s.id)}
                  value={field.value}
                  onChange={(_, v) => field.onChange(v)}
                  getOptionLabel={(id) =>
                    staffOptions.find((s) => s.id === id)?.name ?? 'Staff member'
                  }
                  loading={staff.isLoading}
                  disabled={!can('staff.view')}
                  renderValue={(value, getItemProps) =>
                    value.map((id, index) => {
                      const { key, ...rest } = getItemProps({ index });
                      return (
                        <Chip
                          key={key}
                          size="small"
                          label={staffOptions.find((s) => s.id === id)?.name ?? 'Staff member'}
                          {...rest}
                        />
                      );
                    })
                  }
                  renderInput={(params) => <TextField {...params} label="Notify staff members" />}
                />
              )}
            />
            {info.contact && (
              <Controller
                name="notifyContact"
                control={control}
                render={({ field, fieldState }) => (
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        kind.startsWith('BILL') || kind === 'PO_OVERDUE'
                          ? 'Also message the supplier'
                          : 'Also message the customer'
                      }
                    />
                    {fieldState.error && (
                      <FormHelperText error>{fieldState.error.message}</FormHelperText>
                    )}
                  </Box>
                )}
              />
            )}

            <Box>
              <Controller
                name="template"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    inputRef={templateRef}
                    label="Message (optional)"
                    multiline
                    minRows={3}
                    fullWidth
                    placeholder={kindCatalog?.defaultBody ?? 'Leave empty to use the standard text'}
                    error={Boolean(fieldState.error) || unknown.length > 0}
                    helperText={
                      fieldState.error?.message ??
                      (unknown.length
                        ? `Not available for this reminder: ${unknown.map((u) => `{{${u}}}`).join(', ')}`
                        : 'Leave empty to use the standard text')
                    }
                  />
                )}
              />
              {placeholders.length > 0 && (
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ alignSelf: 'center', mr: 0.5 }}
                  >
                    Insert:
                  </Typography>
                  {placeholders.map((ph) => (
                    <Chip
                      key={ph}
                      size="small"
                      variant="outlined"
                      label={`{{${ph}}}`}
                      onClick={() => insertPlaceholder(ph)}
                      sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Active"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={save.isPending}>
            {editing ? 'Save' : 'Create reminder'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
